"use client"

import Link from "next/link"
import { ArrowRight, Route } from "lucide-react"
import { trackEvent } from "@/lib/analytics"

interface MowingStripeStudioCtaProps {
  grassType: string
  season?: string
  mowerType: "rotary" | "reel" | "robotic"
}

export function MowingStripeStudioCta({
  grassType,
  season,
  mowerType,
}: MowingStripeStudioCtaProps) {
  const params = new URLSearchParams({
    grass: grassType,
    source: "mowing_calculator",
    mower: mowerType,
  })
  if (season) params.set("season", season)

  const href = `/games/lawn-stripe-studio?${params.toString()}`

  return (
    <aside
      className="relative isolate overflow-hidden rounded-2xl border border-[#79985a] bg-[#eef4d5] px-5 py-5 text-[#173126] shadow-[0_18px_50px_rgba(52,84,45,0.12)] sm:px-6"
      data-testid="mowing-stripe-studio-cta"
      aria-labelledby="mowing-stripe-studio-title"
    >
      <div
        className="pointer-events-none absolute inset-y-0 right-0 -z-10 w-2/5 opacity-55"
        aria-hidden="true"
        style={{
          background:
            "repeating-linear-gradient(135deg, #8ab04f 0 18px, #b8d66a 18px 36px)",
          clipPath: "polygon(28% 0, 100% 0, 100% 100%, 0 100%)",
        }}
      />
      <div className="max-w-xl">
        <p className="font-mono text-[0.68rem] font-bold uppercase tracking-[0.18em] text-[#55723f]">
          Try the height in motion
        </p>
        <h2
          id="mowing-stripe-studio-title"
          className="mt-2 text-xl font-black tracking-[-0.025em] text-[#173126] sm:text-2xl"
        >
          Can you stripe this {grassType} lawn?
        </h2>
        <p className="mt-2 max-w-lg text-sm leading-6 text-[#3f5738]">
          Mow a tiny yard, keep the passes clean, and see how your route affects the finish.
        </p>
        <Link
          href={href}
          onClick={() => {
            trackEvent("lawn_stripe_game_entry_clicked", {
              source: "mowing_calculator",
              grass_type: grassType,
              season,
              mower_type: mowerType,
              schema_version: 1,
            })
          }}
          className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-full bg-[#f2c94c] px-4 py-2 text-sm font-extrabold text-[#173126] shadow-[0_7px_0_#b78922] transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_9px_0_#b78922] focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-[#173126] active:translate-y-1 active:shadow-[0_3px_0_#b78922]"
        >
          <Route className="size-4" aria-hidden="true" />
          Open Lawn Stripe Studio
          <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
      </div>
    </aside>
  )
}
